import { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ArrowUpDown } from 'lucide-react';

interface SiteColumn {
  id: number;
  name: string;
}

interface AvailabilityCell {
  value: boolean;
  isModified: boolean;
  itemId: number;
  siteId: number;
}

export function getColumns(
  sites: SiteColumn[],
  onAvailabilityChange: (itemId: number, siteId: number, isAvailable: boolean) => void,
  selectedSites: Set<number>,
  onSiteSelectionChange: (sites: Set<number>) => void
): ColumnDef<any>[] {
  
  // Fixed columns for item details
  const fixedColumns: ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Item Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div>{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'category_name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Category
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-muted-foreground">{row.getValue('category_name') || '-'}</div>
      ),
    },
  ];

  // Dynamic columns for each site
  const siteColumns: ColumnDef<any>[] = sites.map(site => ({
    id: `site_${site.id}`,
    accessorKey: `site_${site.id}`,
    header: () => {
      const isSelected = selectedSites.has(site.id);
      
      const handleSiteSelection = (checked: boolean) => {
        const newSelection = new Set(selectedSites);
        if (checked) {
          newSelection.add(site.id);
        } else {
          newSelection.delete(site.id);
        }
        onSiteSelectionChange(newSelection);
      };
      
      return (
        <div className="flex flex-col items-center gap-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleSiteSelection}
            aria-label={`Select all for ${site.name}`}
            className="mb-1"
          />
          <span className="text-xs font-medium text-center">
            {site.name}
          </span>
        </div>
      );
    },
    cell: ({ getValue }) => {
      const cellData = getValue() as AvailabilityCell;
      
      return (
        <div className="flex justify-center">
          <Checkbox
            checked={cellData.value}
            onCheckedChange={(checked) => 
              onAvailabilityChange(cellData.itemId, cellData.siteId, checked as boolean)
            }
            aria-label={`Availability for item ${cellData.itemId} at site ${cellData.siteId}`}
            className={cellData.isModified ? 'data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500' : ''}
          />
        </div>
      );
    },
    enableSorting: false,
  }));

  return [...fixedColumns, ...siteColumns];
}